import Dexie, { Table } from 'dexie'

import { PosSync } from '@/types'

/**
 * The device's own database (POS.md §15.2): screens read and write only here; the sync engine moves rows to and from
 * the server. Tables are named after the sync keys. Every row has `id` (the server `_id`, or its `temp_id` until the
 * server has one), `synced` and the server fields as they came.
 */
type Row = PosSync.LocalRow

/** Pulled from the server, never written on the device. */
export const PULL_ONLY_TABLES = [
  'branches',
  'terminals',
  'attributes',
  'attribute_values',
  'price_books',
  'branch_products',
  'stocks',
  'users',
  'roles',
  'offers',
  // Tiers by reward points, for the member discount (POS.md §7.3).
  'membership_tiers',
] as const

/** Written on the device and pushed, in this order: a record's parents go first (§5.2). */
export const PUSH_TABLES = [
  // The catalog is edited on the device (POS.md §18.7 Q2) and pulled like the rest: parents first.
  'categories',
  'brands',
  'measurement_units',
  'products',
  'product_variations',
  // Branch prices and price-book prices of this branch (Q4).
  'branch_product_variations',
  'customers',
  'terminal_shifts',
  'terminal_cash_in_outs',
  'orders',
  'order_items',
  'order_returns',
  'order_return_items',
  'payment_logs',
  'credit_repayments',
  'stock_adjustments',
] as const

export type TableName = (typeof PULL_ONLY_TABLES)[number] | (typeof PUSH_TABLES)[number]

const BASE = 'id, _id, temp_id, synced'

const SCHEMA: Record<TableName, string> = {
  branches: BASE,
  terminals: `${BASE}, branch_id`,
  categories: `${BASE}, parent_id`,
  brands: BASE,
  measurement_units: BASE,
  attributes: BASE,
  attribute_values: `${BASE}, attribute_id`,
  products: `${BASE}, category_id`,
  product_variations: `${BASE}, product_id, sku`,
  price_books: BASE,
  branch_products: `${BASE}, product_id`,
  branch_product_variations: `${BASE}, product_variation_id, price_book_id`,
  stocks: `${BASE}, product_variation_id`,
  users: BASE,
  roles: BASE,
  offers: BASE,
  membership_tiers: BASE,
  customers: `${BASE}, phone`,
  terminal_shifts: `${BASE}, terminal_id`,
  terminal_cash_in_outs: `${BASE}, shift_id, shift_temp_id`,
  orders: `${BASE}, created_at`,
  order_items: `${BASE}, order_id, order_temp_id`,
  order_returns: `${BASE}, order_id, order_temp_id`,
  order_return_items: `${BASE}, order_return_temp_id`,
  payment_logs: `${BASE}, order_id, order_temp_id`,
  credit_repayments: BASE,
  stock_adjustments: BASE,
}

export interface MetaRow {
  key: string
  value: unknown
}

class PosDatabase extends Dexie {
  meta!: Table<MetaRow, string>

  constructor() {
    super('iws-pos')
    // v2 roles, v3 offers, v4 membership_tiers, v5 stock_adjustments.
    this.version(5).stores({ meta: 'key', ...SCHEMA })
  }
}

export const db = new PosDatabase()

export const rows = (name: TableName): Table<Row, string> => db.table<Row, string>(name)

export const meta = {
  async get<T>(key: string): Promise<T | undefined> {
    return (await db.meta.get(key))?.value as T | undefined
  },
  async set(key: string, value: unknown) {
    await db.meta.put({ key, value })
  },
}
